<script setup lang="ts">
import type { ICareerSubMenu } from '~/domains/career/components/career-subMenu/career-subMenu.types'
import { CAREER_SUB_MENU_ITEMS } from '~/domains/career/components/career-subMenu/career-subMenu.consts'

const { t } = useI18n()
const { isTrueTablet } = useBSBreakpoints()
const { setLazyScrollTo } = useLazyScroll(160)

const activeIndex = ref(0)
const menuItems: ICareerSubMenu = CAREER_SUB_MENU_ITEMS
let sections: HTMLElement[] = []

const scrollToSection = (hash: string) => {
  setLazyScrollTo(`#${t(hash)}`)
}

const updateActiveSection = () => {
  if (sections.length === 0) return

  const viewportCenter = window.innerHeight / 2
  let closestIndex = 0
  let minDelta: number | null = null

  sections.forEach((el, idx) => {
    const rect = el.getBoundingClientRect()
    const elCenter = rect.top + rect.height / 2
    const delta = Math.abs(elCenter - viewportCenter)

    if (minDelta === null || delta < minDelta) {
      minDelta = delta
      closestIndex = idx
    }
  })

  activeIndex.value = closestIndex
}

onMounted(() => {
  sections = menuItems
    .map(i => document.querySelector(`#${t(i.hash)}`) as HTMLElement | null)
    .filter((el): el is HTMLElement => !!el)

  updateActiveSection()
  window.addEventListener('scroll', updateActiveSection)
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateActiveSection)
})
</script>

<template>
  <nav
    v-if="isTrueTablet"
    class="career-scroll-progress"
    role="navigation"
    aria-label="Nawigacja między sekcjami"
  >
    <div class="career-scroll-progress__dots">
      <button
        v-for="(item, index) in menuItems"
        :key="index"
        :class="[
          'career-scroll-progress__dot',
          { 'career-scroll-progress__dot--active': index === activeIndex }
        ]"
        :aria-label="t(item.label)"
        :aria-current="index === activeIndex ? 'true' : 'false'"
        @click="scrollToSection(item.hash)"
      />
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.career-scroll-progress {
  position: fixed;
  top: 50%;
  right: 2rem;
  z-index: 100;
  transform: translateY(-50%);

  &__dots {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  &__dot {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 1.6rem;
    width: 1.6rem;
    min-height: 1.6rem;
    height: 1.6rem;
    background: transparent;
    border: 0.2rem solid $gray-400;
    border-radius: 50%;
    cursor: pointer;
    transition: border-color 0.3s ease;

    &:hover {
      border-color: $gray-500;
    }

    &:focus {
      border: 0.2rem solid $gray-400;
      outline: none;
    }

    &:focus-visible {
      outline: 0.2rem solid $purple-400;
      outline-offset: 0.2rem;
    }

    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0.95rem;
      height: 0.95rem;
      background: $purple-500;
      border-radius: 50%;
      transform: translate(-50%, -50%);
      opacity: 0;
      content: '';
      transition: background-color 0.3s ease, opacity 0.3s ease;
      pointer-events: none;
    }

    &--active {
      &::after {
        opacity: 1;
      }
    }
  }
}

@include atM {
  .career-scroll-progress {
    right: 4rem;
  }
}
</style>

